// Phase 2 — Zustand store for the new video draft preview-then-commit flow.
//
// Holds the current draft state, polls the backend every 5s while the
// director is running, and provides actions for create / approve / reject.
//
// Persistence: while a draft is in flight (status = directing) we save the
// reference_id to localStorage so the user can refresh the page and resume.
//
// This store is INDEPENDENT of the legacy videoGenerationStore. The
// Step2VideoStudio component decides which one to use based on a feature flag.

import { create } from "zustand";
import toast from "react-hot-toast";
import {
  approveVideoDraft,
  createVideoDraft,
  editVideoDraftScript,
  generateVideoDraftAudio,
  getVideoDraft,
  regenerateVideoDraftImage,
  regenerateVideoDraftStyle,
  rejectVideoDraft,
  setVideoDraftAudioMode,
} from "@/services/ads/videos/videoDraftService";
import {
  VIDEO_DRAFT_STATUS,
  type CreateVideoDraftRequest,
  type GenerateDraftAudioRequest,
  type RegenerateDraftImageTarget,
  type VideoDraftResponse,
} from "@/services/ads/videos/videoDraft.types";

const STORAGE_KEY = "fluxi_video_draft_in_flight_v2";
const LEGACY_STORAGE_KEYS = ["fluxi_video_draft_in_flight"];

// Streaming UX (Phase 2 V3): polling every 2s instead of 5s so partial
// outputs (base image, director payload) are surfaced to the preview
// component within a couple of seconds of landing in the DB. This lets
// the UI render the base image as soon as it's ready (~21s) without
// waiting up to 5s extra for the next poll tick.
const POLL_INTERVAL_MS = 2_000;
// Director should respond in ~30s. B-roll voice-over can also generate a
// multi-image preview pack, so with 2s polling this gives it 5 minutes max.
const MAX_POLL_ATTEMPTS_DIRECTOR = 150;
// Post-approve: imagen base ~25s + Kling render ~5min × 2 + merge ~30s.
// Total ~11 min realistic, 15 min worst case with FAL retries.
const MAX_POLL_ATTEMPTS_RENDER = 180;

// Phase 5.2: nuevos estados post-approve para que la pantalla muestre la
// imagen base apenas existe y luego transicione naturalmente al video final
// sin volver al gallery legacy.
type DraftStoreStatus =
  | "idle"
  | "directing" // director Gemini corriendo
  | "ready" // preview listo, esperando approve/reject
  | "approving" // approve en flight
  | "rejecting" // reject en flight
  | "editing_script" // Phase 5.4 — edit-script en flight (sin polling)
  | "regenerating_image" // Phase 5.4 — regenerate-image en flight (sin polling)
  | "regenerating_style" // Phase 5.4 — regenerate-style en flight + polling hasta que vuelva a ready
  | "generating_audio" // Phase 2 V2 — TTS en flight para modeling voice-over
  | "rendering" // post-approve: generating_image | waiting_scenes | merging
  | "finished" // video final listo, polling apagado
  | "done" // estado terminal limpio (post-reject)
  | "error";

interface VideoDraftState {
  /** Internal state of the store (not the same as draft.status). */
  storeStatus: DraftStoreStatus;
  /** The current draft response from the backend. */
  draft: VideoDraftResponse | null;
  /** True while a network call is in flight. */
  isLoading: boolean;
  /** Error message to surface in the UI. */
  errorMessage: string | null;

  // Actions
  startDraft: (payload: CreateVideoDraftRequest) => Promise<boolean>;
  approve: () => Promise<boolean>;
  reject: () => Promise<boolean>;
  /** Phase 5.4: edita el script inline. No regenera imagen ni cuenta como regen. */
  editScript: (scriptPartA: string, scriptPartB?: string | null) => Promise<boolean>;
  /**
   * Phase 5.4: regenera la imagen base. Cuenta como 1 regen.
   * Phase 6 v2: para drafts UGC se puede pasar `target` para regenerar
   * solo una imagen específica (portrait, scene_a, scene_b) en lugar
   * de todas. Kling drafts ignoran el target. Default: "all".
   */
  regenerateImage: (
    instruction?: string | null,
    target?: RegenerateDraftImageTarget | null
  ) => Promise<boolean>;
  /**
   * Phase 5.4: cambia el creative pattern. Cuenta como 1 regen, vuelve a
   * directing. Opcionalmente se le puede pasar una `instruction` en
   * lenguaje natural ("más enojado", "menos dramático") que el director
   * usa como hint para ajustar el tono. El backend la pasa como
   * user_instruction al LLM.
   */
  regenerateStyle: (patternKey?: string | null, instruction?: string | null) => Promise<boolean>;
  /**
   * Phase 2 V2 — generate/regenerate the voice-over track with the chosen
   * voice and (optionally) user-edited script. Only valid for
   * product-modeling-voiceover drafts. Each call overwrites the current
   * audio_url on the server; orphans are fine. Voice swaps are free (no
   * regen counter increment).
   */
  generateAudio: (payload: GenerateDraftAudioRequest) => Promise<boolean>;
  /**
   * Phase 2 V4b (Apr 20 2026) — change the draft's audio_mode AFTER
   * creation. Used by the preview to swap between "professional" (curated
   * voices) and "own" (user-uploaded audio). When switching to "own",
   * `uploadedAudioUrl` is required and the caller must have uploaded
   * the file to S3 beforehand. The endpoint only works in
   * READY_FOR_REVIEW; transitioning to "fast" is not supported at this
   * stage and must be chosen at draft creation time.
   */
  setAudioMode: (
    mode: "professional" | "own",
    uploadedAudioUrl?: string | null
  ) => Promise<boolean>;
  reset: () => void;
  /** Hydrates a backend draft into the modal store after a gallery/page refresh rescue. */
  hydrateDraft: (draft: VideoDraftResponse) => void;
  /** Internal — resumes polling after a page refresh if there's an in-flight draft. */
  resumeFromStorage: () => void;
}

let pollingHandle: ReturnType<typeof setInterval> | null = null;
let pollingAttempts = 0;

function stopPolling() {
  if (pollingHandle) {
    clearInterval(pollingHandle);
    pollingHandle = null;
  }
  pollingAttempts = 0;
}

function persistInFlight(referenceId: string) {
  try {
    localStorage.setItem(STORAGE_KEY, referenceId);
  } catch {
    // ignore — localStorage may be unavailable
  }
}

function clearInFlight() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
}

function isProgressivePreviewStillPreparing(resp: VideoDraftResponse) {
  if (resp.style_id !== "product-modeling-voiceover") return false;
  const refs = resp.reference_images?.filter(Boolean) ?? [];
  return refs.length < 4;
}

function loadInFlight(): string | null {
  try {
    LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export const useVideoDraftStore = create<VideoDraftState>((set, get) => {
  const isPostApproveStatus = (resp: VideoDraftResponse) =>
    resp.status === VIDEO_DRAFT_STATUS.GENERATING_IMAGE ||
    resp.status === VIDEO_DRAFT_STATUS.WAITING_SCENES ||
    resp.status === VIDEO_DRAFT_STATUS.MERGING ||
    resp.status === VIDEO_DRAFT_STATUS.FINISHED ||
    Boolean(resp.approved_at);

  const handlePolledResponse = (resp: VideoDraftResponse) => {
    set({ draft: resp });

    switch (resp.status) {
      case VIDEO_DRAFT_STATUS.READY_FOR_REVIEW:
        // If approved_at is already set we already moved past review. The
        // backend uses ready_for_review as the "preview ready" state, but
        // approve transitions to generating_image so we don't normally re-enter
        // here after approve. Defensive though.
        if (resp.approved_at) {
          // already approved, treat as rendering
          return;
        }
        if (!isProgressivePreviewStillPreparing(resp)) {
          stopPolling();
        }
        // Do NOT clearInFlight() here — keep the reference_id in localStorage
        // so the user can reload the page and resume the preview. Only clear
        // on terminal states (ERROR, REJECTED, FINISHED).
        set({ storeStatus: "ready" });
        return;

      case VIDEO_DRAFT_STATUS.ERROR:
        stopPolling();
        clearInFlight();
        set({
          storeStatus: "error",
          errorMessage:
            resp.error_message ||
            "Algo falló durante la creación del preview. No te cobramos nada.",
        });
        return;

      case VIDEO_DRAFT_STATUS.REJECTED:
        stopPolling();
        clearInFlight();
        set({ storeStatus: "done" });
        return;

      case VIDEO_DRAFT_STATUS.FINISHED:
        // Final video ready. Stop polling and let the UI render the player.
        stopPolling();
        clearInFlight();
        set({ storeStatus: "finished" });
        return;

      case VIDEO_DRAFT_STATUS.GENERATING_IMAGE:
      case VIDEO_DRAFT_STATUS.WAITING_SCENES:
      case VIDEO_DRAFT_STATUS.MERGING:
        // Post-approve render in progress. Stay in rendering state and keep
        // polling so the UI picks up base_image_url and eventually video_url.
        set({ storeStatus: "rendering" });
        return;

      case VIDEO_DRAFT_STATUS.DIRECTING:
      case VIDEO_DRAFT_STATUS.MEDIA_GENERATING:
        // keep polling, director still working
        return;

      default:
        // Unexpected state, stop polling and surface as error.
        stopPolling();
        clearInFlight();
        set({
          storeStatus: "error",
          errorMessage: `Estado desconocido del preview: ${resp.status}`,
        });
    }
  };

  const startPolling = (referenceId: string, maxAttempts: number) => {
    stopPolling();
    pollingAttempts = 0;

    pollingHandle = setInterval(async () => {
      pollingAttempts += 1;
      if (pollingAttempts > maxAttempts) {
        stopPolling();
        clearInFlight();
        set({
          storeStatus: "error",
          errorMessage:
            "Está tardando más de lo esperado. Si algo falló, tus créditos serán devueltos automáticamente.",
        });
        return;
      }

      const { res } = await getVideoDraft(referenceId);
      if (res) handlePolledResponse(res);
      // If error, just continue polling — could be a transient network blip.
    }, POLL_INTERVAL_MS);
  };

  return {
    storeStatus: "idle",
    draft: null,
    isLoading: false,
    errorMessage: null,

    async startDraft(payload) {
      stopPolling();
      clearInFlight();
      set({ isLoading: true, errorMessage: null, draft: null, storeStatus: "directing" });

      const { res, error } = await createVideoDraft(payload);

      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "error" });
        return false;
      }

      set({ draft: res });
      persistInFlight(res.reference_id);

      // If the server already returned ready_for_review (synchronous fast path),
      // skip polling entirely. Otherwise start polling.
      if (res.status === VIDEO_DRAFT_STATUS.READY_FOR_REVIEW) {
        set({ storeStatus: "ready" });
        if (isProgressivePreviewStillPreparing(res)) {
          startPolling(res.reference_id, MAX_POLL_ATTEMPTS_DIRECTOR);
        }
      } else {
        startPolling(res.reference_id, MAX_POLL_ATTEMPTS_DIRECTOR);
      }

      return true;
    },

    async approve() {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "approving", errorMessage: null });
      const { res, error } = await approveVideoDraft(draft.reference_id);
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      // Phase 5.2: instead of closing the modal after approve, transition into
      // rendering and resume polling so the UI can show the base image as soon
      // as it's generated and then the final video player. The render typically
      // takes 5-7 minutes total — we cap at 12 minutes worst case.
      set({ draft: res, storeStatus: "rendering" });
      persistInFlight(draft.reference_id);
      startPolling(draft.reference_id, MAX_POLL_ATTEMPTS_RENDER);
      return true;
    },

    async reject() {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "rejecting", errorMessage: null });
      const { res, error } = await rejectVideoDraft(draft.reference_id);
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "done" });
      clearInFlight();
      toast.success("Preview cancelado.");
      return true;
    },

    async editScript(scriptPartA, scriptPartB) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "editing_script", errorMessage: null });
      const { res, error } = await editVideoDraftScript(draft.reference_id, {
        script_part_a: scriptPartA,
        script_part_b: scriptPartB ?? null,
      });
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "ready" });
      toast.success("Guion actualizado.");
      return true;
    },

    async regenerateImage(instruction, target) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "regenerating_image", errorMessage: null });
      const { res, error } = await regenerateVideoDraftImage(draft.reference_id, {
        instruction: instruction ?? null,
        target: target ?? null,
      });
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "ready" });
      toast.success("Imagen regenerada.");
      return true;
    },

    async regenerateStyle(patternKey, instruction) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, storeStatus: "regenerating_style", errorMessage: null });
      const { res, error } = await regenerateVideoDraftStyle(draft.reference_id, {
        pattern_key: patternKey ?? null,
        instruction: instruction ?? null,
      });
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      // El backend devolvió el draft transicionado a directing. Resume polling
      // hasta que vuelva a ready_for_review (con el nuevo payload e imagen).
      set({ draft: res, storeStatus: "directing" });
      persistInFlight(draft.reference_id);
      startPolling(draft.reference_id, MAX_POLL_ATTEMPTS_DIRECTOR);
      return true;
    },

    async generateAudio(payload) {
      const draft = get().draft;
      if (!draft) return false;

      // The endpoint runs beat-based TTS calls and an S3 upload —
      // typically 4-8 seconds. No polling needed; the response already has
      // the new audio_url. Store stays in generating_audio while in flight
      // so the preview can show a spinner over the audio slot.
      set({ isLoading: true, storeStatus: "generating_audio", errorMessage: null });
      const { res, error } = await generateVideoDraftAudio(draft.reference_id, payload);
      set({ isLoading: false });

      if (error || !res) {
        set({ storeStatus: "ready" });
        return false;
      }

      set({ draft: res, storeStatus: "ready" });
      return true;
    },

    async setAudioMode(mode, uploadedAudioUrl) {
      const draft = get().draft;
      if (!draft) return false;

      set({ isLoading: true, errorMessage: null });
      const { res, error } = await setVideoDraftAudioMode(draft.reference_id, {
        audio_mode: mode,
        audio_url: uploadedAudioUrl ?? null,
      });
      set({ isLoading: false });

      if (error || !res) return false;

      // Reuse the same ready status as other post-edit actions so the
      // preview re-renders with the new audio_mode without transitioning
      // to any pending/rendering state. The OwnVoicePanel (when mode="own")
      // or the VoiceLibrary (mode="professional") swap driven by
      // draft.audio_mode.
      set({ draft: res, storeStatus: "ready" });
      toast.success(mode === "own" ? "Voz subida lista." : "Voces profesionales activas.");
      return true;
    },

    reset() {
      stopPolling();
      clearInFlight();
      set({
        storeStatus: "idle",
        draft: null,
        isLoading: false,
        errorMessage: null,
      });
    },

    hydrateDraft(draft) {
      stopPolling();
      set({ draft, errorMessage: null, isLoading: false });
      persistInFlight(draft.reference_id);
      handlePolledResponse(draft);
      if (
        draft.status === VIDEO_DRAFT_STATUS.DIRECTING ||
        draft.status === VIDEO_DRAFT_STATUS.MEDIA_GENERATING ||
        (draft.status === VIDEO_DRAFT_STATUS.READY_FOR_REVIEW && isProgressivePreviewStillPreparing(draft))
      ) {
        startPolling(draft.reference_id, MAX_POLL_ATTEMPTS_DIRECTOR);
      } else if (
        draft.status === VIDEO_DRAFT_STATUS.GENERATING_IMAGE ||
        draft.status === VIDEO_DRAFT_STATUS.WAITING_SCENES ||
        draft.status === VIDEO_DRAFT_STATUS.MERGING
      ) {
        startPolling(draft.reference_id, MAX_POLL_ATTEMPTS_RENDER);
      }
    },

    resumeFromStorage() {
      const referenceId = loadInFlight();
      if (!referenceId) return;
      // Try to recover the draft. If the backend says it's still directing,
      // resume polling. If it's already ready/error/rejected, jump straight
      // to that state. Post-approve drafts are deliberately NOT resumed here:
      // the gallery-side videoInFlightStore owns those cards, and keeping
      // this singleton store in "rendering" blocks the user from opening a
      // new Create Video modal while another video is already rendering.
      set({ storeStatus: "directing" });
      getVideoDraft(referenceId).then(({ res }) => {
        if (res) {
          if (isPostApproveStatus(res)) {
            clearInFlight();
            stopPolling();
            set({
              storeStatus: "idle",
              draft: null,
              isLoading: false,
              errorMessage: null,
            });
            return;
          }

          handlePolledResponse(res);
          if (
            res.status === VIDEO_DRAFT_STATUS.DIRECTING ||
            res.status === VIDEO_DRAFT_STATUS.MEDIA_GENERATING ||
            (res.status === VIDEO_DRAFT_STATUS.READY_FOR_REVIEW && isProgressivePreviewStillPreparing(res))
          ) {
            startPolling(referenceId, MAX_POLL_ATTEMPTS_DIRECTOR);
          } else if (
            res.status === VIDEO_DRAFT_STATUS.GENERATING_IMAGE ||
            res.status === VIDEO_DRAFT_STATUS.WAITING_SCENES ||
            res.status === VIDEO_DRAFT_STATUS.MERGING
          ) {
            startPolling(referenceId, MAX_POLL_ATTEMPTS_RENDER);
          }
        } else {
          // Backend doesn't know about it anymore; clear local state.
          clearInFlight();
          set({ storeStatus: "idle" });
        }
      });
    },
  };
});
